/**
 * lib/views/javascripts/shell/head.js — what the Mach33 shell does in <head>,
 * before the body parses, on every desktop page: the server's flags as
 * globals, the one visibility-aware poller, the embed stamp, the 401 → /login
 * redirect, the arrival fade, and the broken-shell banner (ADR 0082 P3, lv-1).
 *
 * Moved as is from the four inline scripts lib/vega-shell-extras.js
 * headExtras() wrote into <head>. They ran while <head> parsed, and they still
 * must: the embed and transition stamps land before first paint, the fetch
 * wrapper is in place before any other script fetches, and classic scripts
 * other owners still inline read __m33Poll and the flags as they parse. So
 * runShellHead() runs from a head entry (lib/views/javascripts/head/shell.js),
 * built as one classic script that lib/views/htmls/shell/head.ejs loads with
 * a blocking <script src>, never from a module. The server's values arrive as
 * boot data (#m33-head-boot), not pasted into code (ADR 0082 D7).
 *
 * Contents: view
 * Ontology: Code object M33C-0728 (immutable ref — assigned once, never edit)
 * Team: J. Brant Arseneau & the Mach33 Agents.
 */

// ── Isolation ────────────────────────────────────────────────────────────
// Each part was its own <script>, where an error stopped only that script.
function isolated(tag, run) {
  try { run(); }
  catch (e) { console.error('[shell:head:' + tag + '] failed:', String(e && e.stack || e).slice(0, 400)); }
}

// ── Flags ────────────────────────────────────────────────────────────────
// Server-side feature flags, synchronously available to every script that
// runs after this one: the composer branches on
// window.__M33_FLAGS.canonicalChatText without a fetch round-trip. The
// app-kind suffix the header paints thin after the agent's name ('Research',
// 'IDE', …) and an instance's SELF.header.workstation:false, which the
// workstation icon and panel read to stand down before touching the header
// or the right rail, come the same way when the host sets them.
export function setFlags(boot) {
  window.__M33_FLAGS = window.__M33_FLAGS || {};
  window.__M33_FLAGS.canonicalChatText = boot.flags.canonicalChatText === true;
  window.__M33_FLAGS.canonicalChatVoice = boot.flags.canonicalChatVoice === true;
  window.__M33_LIVE_HOST__ = boot.liveHost;
  window.__M33_AGENT_NAME__ = boot.agentName;
  window.__M33_REALTIME_VOICE__ = boot.realtimeVoice;
  if (typeof boot.appKind === 'string') window.__M33_APP_KIND__ = boot.appKind;
  if (boot.workstation === false) window.__M33_WORKSTATION__ = false;
}

// ── Polling that stops when nobody is looking ────────────────────────────
// A background tab has no reason to query a database. One forgotten tab
// left open on a UAT surface was measured issuing 54 nav-count polls in a
// day, each one a query, and that traffic is what pushed the shared
// instance to 'Connection terminated due to connection timeout' — a real
// outage produced by a window nobody was reading.
//
// Defined here, in <head>, because every polling script in the shell runs
// after this one and there must be exactly ONE of these (rule 11).
//
// The contract: while the page is hidden the work does not run; on
// becoming visible it runs IMMEDIATELY, so returning to a tab never shows
// stale counts waiting out the rest of an interval.
export function installPoll() {
  window.__m33Poll = function (fn, ms) {
    var timer = null;
    function tick() { if (!document.hidden) { try { fn(); } catch (_) {} } }
    timer = setInterval(tick, ms);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) tick();
    });
    return { stop: function () { clearInterval(timer); } };
  };
}

// ── Embed stamp ──────────────────────────────────────────────────────────
// Teams embed contract: ?host=<vega|patton|myassistant|standalone>
// Stamp <html> with data-host + data-compact BEFORE the body parses so
// CSS (in compact mode) can hide the standalone chrome on first paint —
// no flash of standalone shell inside an embedding iframe. Used by Teams
// and harmless on Vega/Patton (no compact CSS rules target their hosts).
export function stampHost() {
  try {
    var p = new URLSearchParams(location.search);
    var raw = (p.get('host') || '').toLowerCase();
    var host = /^[a-z][a-z0-9-]{0,31}$/.test(raw) ? raw : '';
    var known = { vega:1, patton:1, myassistant:1 };
    var resolved = known[host] ? host : 'standalone';
    var compact = !!known[host] || p.get('compact') === '1';
    document.documentElement.setAttribute('data-host', resolved);
    if (compact) document.documentElement.setAttribute('data-compact', '1');
  } catch (_) {}
}

// ── 401 → /login ─────────────────────────────────────────────────────────
// When the server-side session expires, background fetch / SSE calls
// return { ok:false, error:'auth_required' } JSON. Without this, that
// raw JSON sometimes bleeds into the visible UI instead of bouncing
// the user to the login screen. Wrap window.fetch at the very top
// of the document so EVERY fetch — ours, vendor scripts, future
// additions — gets the redirect-on-auth_required behaviour for free.
// Guard against infinite loops on /login itself + on POSTs that
// have body-stream issues by only triggering on the clone().
export function installAuthRedirect() {
  if (window.__m33_auth_intercept) return;
  window.__m33_auth_intercept = true;
  var orig = window.fetch;
  if (typeof orig !== 'function') return;
  window.fetch = function() {
    var p = orig.apply(this, arguments);
    return p.then(function (r) {
      try {
        if (r && r.status === 401) {
          var ct = (r.headers && r.headers.get && r.headers.get('content-type')) || '';
          if (ct.indexOf('json') !== -1 && location.pathname !== '/login') {
            // Clone so the original consumer can still .json() / .text() the body.
            r.clone().json().then(function (j) {
              if (j && j.error === 'auth_required') {
                var next = location.pathname + location.search;
                location.replace('/login?next=' + encodeURIComponent(next));
              }
            }).catch(function () {});
          }
        }
      } catch (_) { /* never let our handler break the caller's response */ }
      return r;
    });
  };
}

// ── Arrival fade ─────────────────────────────────────────────────────────
// Hide the body from first paint when this load is a launcher switch,
// so the arrival fade has nothing to flash over. Same-origin switches
// carry a sessionStorage flag; CROSS-ORIGIN ones (another agent's
// subdomain, e.g. relay.33fg.ai) can't — they carry ?m33switch= in
// the URL instead. Either one arms the arrival fade.
export function armArrivalFade() {
  try {
    var armed = false;
    try { if (sessionStorage.getItem('m33_transition_in')) armed = true; } catch (e) {}
    if (!armed && /[?&]m33switch=/.test(location.search)) armed = true;
    if (armed) document.documentElement.setAttribute('data-m33-transitioning', '1');
  } catch (e) { /* storage or URL API unavailable */ }
}

// ── Boot probe ───────────────────────────────────────────────────────────
// Runs once the page finishes loading. If the shell did not define its core
// exports (window.M33 / openEditorialPipelineModal /
// openDraftEditorModal — whichever lives in the page that loaded), the
// shell failed to run. Surface a visible banner so the user knows the page
// is broken instead of clicking dead controls in confusion.
export function watchShellBoot() {
  window.addEventListener('load', function () {
    setTimeout(function () {
      var iifeOk =
           typeof window.openEditorialPipelineModal === 'function'
        || typeof window.openEditorialPipelineInstanceModal === 'function'
        || typeof window.M33 === 'object';
      if (iifeOk) return;
      var b = document.createElement('div');
      b.id = 'm33-iife-banner';
      b.setAttribute('role', 'alert');
      b.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:30000;'
        + 'background:#7a1f1f;color:#fff7f7;padding:10px 16px;'
        + 'font:13px/1.4 -apple-system,Segoe UI,Roboto,sans-serif;'
        + 'box-shadow:0 2px 8px rgba(0,0,0,0.55);text-align:center;';
      b.innerHTML = '<strong>UI is broken:</strong> shell-extras inline script failed to run. '
        + 'Check the server console for "[vega-shell-extras] inline script ... FAILED to parse" and fix the offending block. '
        + 'Modals, profile menu, and pipeline controls are inert until you reload after the fix.';
      document.body.insertBefore(b, document.body.firstChild);
    }, 800);
  });
}

// ── Start ────────────────────────────────────────────────────────────────
// readBoot returns the #m33-head-boot data. In the order the four scripts
// ran; a missing block costs only the flags.
export function runShellHead(readBoot) {
  isolated('flags', function () { setFlags(readBoot()); });
  isolated('poll', installPoll);
  isolated('host', stampHost);
  isolated('auth', installAuthRedirect);
  isolated('fade', armArrivalFade);
  isolated('probe', watchShellBoot);
}
